/**
 * @fileoverview ProfitLossChart - Bar chart with cumulative line overlay
 *
 * Purpose: Display profit/loss per period with cumulative trend line.
 * Features polished styling: rounded bars, subtle grid, gradient line,
 * and styled dots for a refined, on-brand look.
 *
 * Used by: Review phase financial analysis
 */

import {
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from "@/ui/composed/data-display/chart";
import {
  Bar,
  CartesianGrid,
  Cell,
  ComposedChart,
  Line,
  XAxis,
  YAxis,
} from "recharts";
import {
  periodAlignedLayout,
  type PeriodAlignedChartProps,
} from "./periodAlignedLayout";

const config = {
  profitLoss: { label: "Profit / (Loss)", color: "hsl(var(--chart-1))" },
  cumulative: { label: "Cumulative", color: "hsl(var(--chart-2))" },
} satisfies ChartConfig;

const dollarFormatter = (v: number) => `$${(v / 1000).toFixed(0)}k`;

export interface ProfitLossChartProps extends PeriodAlignedChartProps {
  periods: string[];
  profitLoss: (number | null)[];
  cumulativeProfitLoss: (number | null)[];
}

/** Profit/loss bars per period with a cumulative line; nulls leave gaps. */
export function ProfitLossChart({
  periods,
  profitLoss,
  cumulativeProfitLoss,
  plotInsetLeft,
}: ProfitLossChartProps) {
  const layout = periodAlignedLayout(plotInsetLeft, 60);
  // Missing values stay null — a bar is skipped and a line breaks — rather
  // than plotting as zero beside a table that shows a dash (MOR-1282 QA).
  const data = periods.map((period, i) => ({
    period,
    profitLoss: profitLoss[i] ?? null,
    cumulative: cumulativeProfitLoss[i] ?? null,
  }));

  return (
    <ChartContainer config={config} className="min-h-[180px] w-full">
      <ComposedChart data={data} margin={layout.margin}>
        <CartesianGrid
          vertical={false}
          stroke="hsl(var(--border))"
          strokeOpacity={0.5}
        />
        <XAxis
          dataKey="period"
          tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }}
          tickLine={false}
          axisLine={false}
        />
        <YAxis
          tickFormatter={dollarFormatter}
          tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }}
          tickLine={false}
          axisLine={false}
          width={layout.yAxisWidth}
        />
        <ChartTooltip
          content={
            <ChartTooltipContent
              formatter={(v) =>
                typeof v === "number" ? dollarFormatter(v) : "—"
              }
            />
          }
        />
        <ChartLegend content={<ChartLegendContent />} />
        <Bar
          dataKey="profitLoss"
          fill="var(--color-profitLoss)"
          radius={[4, 4, 0, 0]}
        >
          {data.map((entry, i) => (
            <Cell
              key={i}
              fill={
                (entry.profitLoss ?? 0) >= 0
                  ? "hsl(var(--chart-1))"
                  : "hsl(var(--chart-4))"
              }
              radius={[4, 4, 0, 0] as unknown as number}
            />
          ))}
        </Bar>
        <Line
          type="monotone"
          dataKey="cumulative"
          stroke="hsl(var(--chart-2))"
          strokeWidth={2}
          dot={{
            r: 3,
            fill: "hsl(var(--chart-2))",
            strokeWidth: 2,
            stroke: "hsl(var(--background))",
          }}
          activeDot={{ r: 5, strokeWidth: 2 }}
        />
      </ComposedChart>
    </ChartContainer>
  );
}
